import React, { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  ViewColumnsIcon,
  CalendarDaysIcon,
  BoltIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FireIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import taskService from '../../services/taskService';
import { Task, TaskAnalytics, TaskFilterParams } from '../../types/task.types';
import { Button, Tabs, TabPanel } from '../ui';
import TaskFormModal from './TaskFormModal';
import TaskDetailSlideOver from './TaskDetailSlideOver';
import { ConfettiCelebration } from './components';

// Import views (will create these next)
import MyTasksView from './views/MyTasksView';
import AllTasksView from './views/AllTasksView';
import TaskBoardView from './views/TaskBoardView';
import FocusModeView from './views/FocusModeView';

const TaskCommandCenter: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<TaskAnalytics | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showDetailSlideOver, setShowDetailSlideOver] = useState(false);

  // Celebration
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');

  // Streak (mock - would come from API)
  const [streak, setStreak] = useState(5);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, myTasksRes, analyticsRes] = await Promise.all([
        taskService.getTasks({ limit: 100 }),
        taskService.getMyTasks({ limit: 100 }),
        taskService.getTaskAnalytics(),
      ]);
      setTasks(tasksRes.data);
      setMyTasks(myTasksRes.data);
      setAnalytics(analyticsRes);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTaskComplete = async (taskId: number) => {
    try {
      await taskService.completeTask(taskId);
      setShowCelebration(true);
      setCelebrationMessage('Task Completed!');
      loadData();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setShowDetailSlideOver(true);
  };

  const handleTaskCreated = () => {
    setShowCreateModal(false);
    loadData();
  };

  const handleTaskUpdated = () => {
    loadData();
  };

  const tabs = [
    { id: 'my-tasks', label: 'My Tasks', icon: <UserIcon className="h-5 w-5" />, badge: myTasks.length },
    { id: 'all-tasks', label: 'All Tasks', icon: <ClipboardDocumentListIcon className="h-5 w-5" />, badge: tasks.length },
    { id: 'board', label: 'Board', icon: <ViewColumnsIcon className="h-5 w-5" /> },
    { id: 'focus', label: 'Focus Mode', icon: <BoltIcon className="h-5 w-5" /> },
  ];

  const todayTasks = myTasks.filter(t => {
    if (!t.dueDateTime) return false;
    const due = new Date(t.dueDateTime);
    const today = new Date();
    return due.toDateString() === today.toDateString();
  });

  const overdueTasks = myTasks.filter(t => {
    if (!t.dueDateTime || t.status === 'COMPLETED') return false;
    return new Date(t.dueDateTime) < new Date();
  });

  const completedToday = myTasks.filter(t => {
    if (!t.completedAt) return false;
    const completed = new Date(t.completedAt);
    const today = new Date();
    return completed.toDateString() === today.toDateString();
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50/30" data-tour="page-content">
      {/* Celebration */}
      <ConfettiCelebration
        isActive={showCelebration}
        message={celebrationMessage}
        onComplete={() => setShowCelebration(false)}
      />

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Title row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Task Command Center</h1>
                  <p className="text-sm text-gray-500">
                    Welcome back, {user?.firstName || 'there'}! You have {todayTasks.length} tasks due today.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Streak badge */}
                {streak > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full">
                    <FireIcon className="h-4 w-4" />
                    <span className="text-sm font-semibold">{streak}-day streak!</span>
                  </div>
                )}
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                  leftIcon={<PlusIcon className="h-5 w-5" />}
                >
                  New Task
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <StatBox
                icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
                label="Total Tasks"
                value={analytics?.total || 0}
                color="blue"
              />
              <StatBox
                icon={<ClockIcon className="h-5 w-5" />}
                label="Due Today"
                value={todayTasks.length}
                color="yellow"
              />
              <StatBox
                icon={<CheckCircleIcon className="h-5 w-5" />}
                label="Completed"
                value={completedToday.length}
                color="green"
                subtitle="today"
              />
              <StatBox
                icon={<ExclamationTriangleIcon className="h-5 w-5" />}
                label="Overdue"
                value={overdueTasks.length}
                color="red"
                pulse={overdueTasks.length > 0}
              />
            </div>

            {/* Tabs */}
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              variant="boxed"
            />
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <TabPanel tabId="my-tasks" activeTab={activeTab}>
          <MyTasksView
            tasks={myTasks}
            loading={loading}
            onTaskClick={handleTaskClick}
            onTaskComplete={handleTaskComplete}
            onRefresh={loadData}
          />
        </TabPanel>

        <TabPanel tabId="all-tasks" activeTab={activeTab}>
          <AllTasksView
            tasks={tasks}
            loading={loading}
            onTaskClick={handleTaskClick}
            onTaskComplete={handleTaskComplete}
            onRefresh={loadData}
          />
        </TabPanel>

        <TabPanel tabId="board" activeTab={activeTab}>
          <TaskBoardView
            tasks={tasks}
            loading={loading}
            onTaskClick={handleTaskClick}
            onTaskComplete={handleTaskComplete}
            onRefresh={loadData}
          />
        </TabPanel>

        <TabPanel tabId="focus" activeTab={activeTab}>
          <FocusModeView
            tasks={myTasks.filter(t => t.status !== 'COMPLETED')}
            onTaskComplete={handleTaskComplete}
            onTaskClick={handleTaskClick}
          />
        </TabPanel>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <TaskFormModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleTaskCreated}
        />
      )}

      {showDetailSlideOver && selectedTask && (
        <TaskDetailSlideOver
          task={selectedTask}
          onClose={() => {
            setShowDetailSlideOver(false);
            setSelectedTask(null);
          }}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
    </div>
  );
};

// Stat Box Component
interface StatBoxProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  subtitle?: string;
  pulse?: boolean;
}

const colorStyles = {
  blue: 'from-blue-500 to-blue-600 shadow-blue-200',
  green: 'from-green-500 to-green-600 shadow-green-200',
  yellow: 'from-yellow-500 to-yellow-600 shadow-yellow-200',
  red: 'from-red-500 to-red-600 shadow-red-200',
  purple: 'from-purple-500 to-purple-600 shadow-purple-200',
};

const StatBox: React.FC<StatBoxProps> = ({ icon, label, value, color, subtitle, pulse }) => (
  <div className={clsx(
    'relative bg-white rounded-xl p-4 border border-gray-100 shadow-sm',
    'hover:shadow-md transition-shadow duration-200',
    pulse && 'animate-pulse'
  )}>
    <div className={clsx(
      'inline-flex p-2 rounded-lg bg-gradient-to-br text-white shadow-lg mb-3',
      colorStyles[color]
    )}>
      {icon}
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">
        {label}
        {subtitle && <span className="text-xs ml-1">({subtitle})</span>}
      </div>
    </div>
  </div>
);

export default TaskCommandCenter;

